import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { checkDeviceReachability } from '../api/hikvision'

const DOOR_DEFAULTS = {
  name: '',
  host: '',
  port: '443',
  use_https: true,
  notes: '',
  video_enabled: true,
  rtsp_port: '554',
  video_channel: '102',
  hik_username: '',
  hik_password: '',
}

const PANIC_DEFAULTS = {
  name: '',
  host: '',
  port: '80',
  use_https: false,
  notes: '',
  video_enabled: true,
  rtsp_port: '554',
  video_channel: '101',
  hik_username: '',
  hik_password: '',
}

const NVR_DEFAULTS = {
  name: '',
  host: '',
  port: '80',
  use_https: false,
  notes: '',
  video_enabled: true,
  rtsp_port: '554',
  video_channel: '1',
  hik_username: '',
  hik_password: '',
}

const AddHikvisionDeviceModal = ({ isOpen, onClose, onSubmit, isLoading, error, deviceType = 'door' }) => {
  const { t } = useTranslation()
  const isPanic = deviceType === 'panic'
  const isNvr = deviceType === 'nvr'
  const initialDefaults = isPanic ? PANIC_DEFAULTS : isNvr ? NVR_DEFAULTS : DOOR_DEFAULTS

  const [formData, setFormData] = useState(initialDefaults)

  const [errors, setErrors] = useState({})
  const [isChecking, setIsChecking] = useState(false)
  const [checkResult, setCheckResult] = useState(null)

  useEffect(() => {
    if (isOpen) {
      setCheckResult(null)
      setIsChecking(false)
    }
  }, [isOpen])

  if (!isOpen) return null

  const title = isPanic
    ? t('schoolDashboard.panicAddTitle')
    : isNvr
      ? 'Добавить видеорегистратор'
      : 'Добавить устройство контроля доступа'

  const ariaLabel = isPanic
    ? t('schoolDashboard.panicAddModalAria')
    : isNvr
      ? 'Добавить видеорегистратор'
      : 'Добавить устройство Hikvision'

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
    if (name === 'host' || name === 'port' || name === 'use_https' || name === 'hik_username' || name === 'hik_password') {
      setCheckResult(null)
    }
    if (errors[name] || (name === 'hik_password' && errors.hik_username)) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        if (name === 'hik_password') delete newErrors.hik_username
        return newErrors
      })
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Название устройства обязательно'
    }

    if (!formData.host.trim()) {
      newErrors.host = 'Адрес устройства обязателен'
    }

    const portNum = parseInt(formData.port, 10)
    if (Number.isNaN(portNum) || portNum < 1 || portNum > 65535) {
      newErrors.port = 'Порт должен быть от 1 до 65535'
    }

    const rtspPortNum = parseInt(formData.rtsp_port, 10)
    if (formData.video_enabled && (Number.isNaN(rtspPortNum) || rtspPortNum < 1 || rtspPortNum > 65535)) {
      newErrors.rtsp_port = 'RTSP порт должен быть от 1 до 65535'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleCheckReachability = async () => {
    const newErrors = {}
    if (!formData.host.trim()) {
      newErrors.host = t('schoolDashboard.hikCheckHostRequired')
    }
    const portNum = parseInt(formData.port, 10)
    if (Number.isNaN(portNum) || portNum < 1 || portNum > 65535) {
      newErrors.port = t('schoolDashboard.hikCheckPortInvalid')
    }
    const user = formData.hik_username.trim()
    const pass = formData.hik_password
    if ((user && !pass) || (!user && pass)) {
      newErrors.hik_username = t('schoolDashboard.hikCheckCredsPair')
    }
    setErrors((prev) => {
      const next = { ...prev, ...newErrors }
      if (!newErrors.host) delete next.host
      if (!newErrors.port) delete next.port
      if (!newErrors.hik_username) delete next.hik_username
      return next
    })
    if (Object.keys(newErrors).length) return

    setIsChecking(true)
    setCheckResult(null)
    try {
      const body = {
        host: formData.host.trim(),
        port: portNum,
        use_https: formData.use_https,
        device_type: deviceType,
      }
      if (user && pass) {
        body.hik_username = user
        body.hik_password = pass
      }
      const data = await checkDeviceReachability(body)
      setCheckResult(data)
    } catch (err) {
      setCheckResult({
        reachable: false,
        message: err.response?.data?.message || err.message || t('schoolDashboard.hikCheckFailed'),
      })
    } finally {
      setIsChecking(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!validate()) return

    const submitData = {
      name: formData.name.trim(),
      host: formData.host.trim(),
      port: parseInt(formData.port, 10),
      use_https: formData.use_https,
      notes: formData.notes.trim(),
      video_enabled: formData.video_enabled,
      rtsp_port: parseInt(formData.rtsp_port, 10) || 554,
      video_channel: formData.video_channel,
      device_type: deviceType,
    }

    const user = formData.hik_username.trim()
    const pass = formData.hik_password
    if (user || pass) {
      submitData.hik_username = user
      submitData.hik_password = pass
    }

    onSubmit(submitData)
  }

  const handleClose = () => {
    setFormData(initialDefaults)
    setErrors({})
    setCheckResult(null)
    setIsChecking(false)
    onClose()
  }

  const busy = isLoading || isChecking

  const modalContent = (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/70 px-4 py-10 backdrop-blur-2xl"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <div className="pointer-events-none absolute inset-0 bg-brand-radial opacity-50" />

      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl border border-surface-border/40 bg-surface-raised/90 shadow-elevated-lg">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-surface-border/60 bg-surface-raised/95 px-6 py-4 backdrop-blur-sm">
          <h2 className="font-display text-2xl text-white">
            {title}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="inline-flex items-center justify-center rounded-full p-2 text-muted-400 transition hover:bg-surface-base/60 hover:text-white disabled:opacity-50"
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 rounded-lg border border-critical/50 bg-critical/10 px-4 py-3 text-sm text-critical">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-semibold text-muted-300">
                Название <span className="text-critical">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full rounded-lg border bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 ${
                  errors.name ? 'border-critical/50 focus:ring-critical/50' : 'border-surface-border/60 focus:ring-brand-400/50'
                }`}
                placeholder={isNvr ? 'Видеорегистратор школы' : 'Главный вход'}
                disabled={busy}
              />
              {errors.name && <p className="mt-1 text-xs text-critical">{errors.name}</p>}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="mb-2 block text-sm font-semibold text-muted-300">
                  IP-адрес или хост <span className="text-critical">*</span>
                </label>
                <input
                  type="text"
                  name="host"
                  value={formData.host}
                  onChange={handleChange}
                  className={`w-full rounded-lg border bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 ${
                    errors.host ? 'border-critical/50 focus:ring-critical/50' : 'border-surface-border/60 focus:ring-brand-400/50'
                  }`}
                  placeholder="192.168.1.100"
                  disabled={busy}
                />
                {errors.host && <p className="mt-1 text-xs text-critical">{errors.host}</p>}
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-muted-300">Порт</label>
                <input
                  type="number"
                  name="port"
                  value={formData.port}
                  onChange={handleChange}
                  min="1"
                  max="65535"
                  className={`w-full rounded-lg border bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 ${
                    errors.port ? 'border-critical/50 focus:ring-critical/50' : 'border-surface-border/60 focus:ring-brand-400/50'
                  }`}
                  placeholder={isPanic || isNvr ? '80' : '443'}
                  disabled={busy}
                />
                {errors.port && <p className="mt-1 text-xs text-critical">{errors.port}</p>}
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-muted-300">HTTPS</label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="use_https"
                  checked={formData.use_https}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-surface-border/60 bg-surface-base/80 text-brand-400 focus:ring-2 focus:ring-brand-400/50"
                  disabled={busy}
                />
                <span className="text-sm text-muted-400">Использовать HTTPS (рекомендуется)</span>
              </label>
            </div>

            <div className="rounded-2xl border border-surface-border/40 bg-surface-base/40 p-4">
              <p className="mb-3 text-sm font-semibold text-muted-200">
                Учётные данные устройства
              </p>
              <p className="mb-3 text-xs text-muted-500">
                Необязательно. Если не заданы, используется общая пара сервера. Логин и пароль задаются только вместе.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs text-muted-400">Логин</label>
                  <input
                    type="text"
                    name="hik_username"
                    value={formData.hik_username}
                    onChange={handleChange}
                    autoComplete="off"
                    className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white"
                    disabled={busy}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-400">Пароль</label>
                  <input
                    type="password"
                    name="hik_password"
                    value={formData.hik_password}
                    onChange={handleChange}
                    autoComplete="new-password"
                    className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white"
                    disabled={busy}
                  />
                </div>
              </div>
              {errors.hik_username && (
                <p className="mt-2 text-xs text-critical">{errors.hik_username}</p>
              )}
            </div>

            <div className="rounded-2xl border border-surface-border/40 bg-surface-base/40 p-4">
              <label className="mb-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  name="video_enabled"
                  checked={formData.video_enabled}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-surface-border/60 bg-surface-base/80 text-brand-400 focus:ring-2 focus:ring-brand-400/50"
                  disabled={busy}
                />
                <span className="text-sm font-semibold text-muted-200">
                  {isPanic
                    ? t('schoolDashboard.panicAddVideoLabel')
                    : isNvr
                      ? 'Живое видео каналов'
                      : 'Видео с камеры турникета'}
                </span>
              </label>
              <div className={`grid gap-4 ${isNvr ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">RTSP порт</label>
                  <input
                    type="number"
                    name="rtsp_port"
                    value={formData.rtsp_port}
                    onChange={handleChange}
                    min="1"
                    max="65535"
                    className={`w-full rounded-lg border bg-surface-base/80 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 ${
                      errors.rtsp_port ? 'border-critical/50 focus:ring-critical/50' : 'border-surface-border/60 focus:ring-brand-400/50'
                    }`}
                    placeholder="554"
                    disabled={busy || !formData.video_enabled}
                  />
                  {errors.rtsp_port && <p className="mt-1 text-xs text-critical">{errors.rtsp_port}</p>}
                </div>
                {!isNvr && (
                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">Видеоканал</label>
                  <select
                    name="video_channel"
                    value={formData.video_channel}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-400/50"
                    disabled={busy || !formData.video_enabled}
                  >
                    <option value="102">102 — sub (рекомендуется)</option>
                    <option value="101">101 — main</option>
                  </select>
                </div>
                )}
              </div>
              {isNvr && (
                <p className="mt-2 text-xs text-muted-400">
                  Каналы берутся с регистратора автоматически; в реестре не заводятся.
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-muted-300">Примечания</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 focus:ring-brand-400/50"
                placeholder="Дополнительная информация..."
                disabled={busy}
              />
            </div>
          </div>

          {checkResult && (
            <div
              role="status"
              className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                checkResult.reachable
                  ? 'border-positive/50 bg-positive/10 text-positive'
                  : 'border-critical/50 bg-critical/10 text-critical'
              }`}
            >
              <div className="flex items-start gap-2">
                <span className="material-icons text-base">
                  {checkResult.reachable ? 'check_circle' : 'error'}
                </span>
                <div>
                  <p>{checkResult.message}</p>
                  {checkResult.reachable && (checkResult.firmware || checkResult.serial) && (
                    <p className="mt-1 text-xs opacity-80">
                      {[checkResult.firmware, checkResult.serial].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-col-reverse gap-3 border-t border-surface-border/60 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={handleCheckReachability}
              disabled={busy}
              className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-surface-border/60 bg-surface-base/40 px-4 py-2 text-sm font-semibold text-muted-200 transition hover:border-brand-400/40 hover:bg-surface-base/70 hover:text-white disabled:opacity-50"
            >
              {isChecking ? (
                <>
                  <span className="material-icons text-sm animate-spin">progress_activity</span>
                  {t('schoolDashboard.hikChecking')}
                </>
              ) : (
                <>
                  <span className="material-icons text-sm">wifi_tethering</span>
                  {t('schoolDashboard.hikCheckReachability')}
                </>
              )}
            </button>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={busy}
                className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-xl bg-surface-base/60 px-4 py-2 text-sm font-semibold text-muted-300 transition hover:bg-surface-base/80 hover:text-white disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={busy}
                className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-xl bg-brand-400/20 px-4 py-2 text-sm font-semibold text-brand-100 transition hover:bg-brand-400/30 hover:text-white disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <span className="material-icons text-sm animate-spin">refresh</span>
                    Добавление...
                  </>
                ) : (
                  <>
                    <span className="material-icons text-sm">add</span>
                    Добавить
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null
}

export default AddHikvisionDeviceModal
